import { useState } from "react";
import { Task } from "@/hooks/use-tasks";
import { getStatusColor, formatDate } from "@/lib/utils";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardFooter 
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash, Calendar } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface TaskListProps {
  tasks: Task[];
  onComplete?: (id: number, completed: boolean) => void;
  onAdd?: (task: any) => void;
  onEdit?: (id: number, task: any) => void;
  onDelete?: (id: number) => void;
  isLoading?: boolean;
  isPendingAdd?: boolean;
  isPendingEdit?: boolean;
  isPendingComplete?: boolean;
  isPendingDelete?: boolean;
  showAddButton?: boolean;
}

export function TaskList({
  tasks,
  onComplete,
  onAdd,
  onEdit,
  onDelete,
  isLoading,
  isPendingAdd,
  isPendingEdit,
  isPendingComplete,
  isPendingDelete,
  showAddButton = true,
}: TaskListProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [deleteTask, setDeleteTask] = useState<Task | null>(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    dueDate: "",
    priority: "medium",
    recurring: false,
    recurrencePattern: "",
  });

  const openAddDialog = () => {
    setFormData({
      title: "",
      description: "",
      dueDate: "",
      priority: "medium",
      recurring: false,
      recurrencePattern: "",
    });
    setIsAddDialogOpen(true);
  };

  const handleAddSubmit = async () => {
    if (onAdd && formData.title) {
      try {
        // Perform validation
        if (!formData.title.trim()) {
          alert("Title is required");
          return;
        }
        
        // Just pass minimal data - use-tasks.ts will handle the formatting and validation
        const taskData = {
          title: formData.title.trim(),
          description: formData.description ? formData.description.trim() : null,
          priority: formData.priority || "medium",
          dueDate: formData.dueDate || null,
          recurring: Boolean(formData.recurring),
          recurrencePattern: formData.recurring && formData.recurrencePattern ? formData.recurrencePattern.trim() : null
        };
        
        await onAdd(taskData);
        setIsAddDialogOpen(false);
      } catch (error) {
        console.error("Error adding task:", error);
        alert(`Failed to create task. Please ensure you're logged in and try again.`);
      }
    }
  };

  const handleEditClick = (task: Task) => {
    setEditTask(task);
    setFormData({
      title: task.title,
      description: task.description || "",
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split("T")[0] : "",
      priority: task.priority || "medium",
      recurring: Boolean(task.recurring),
      recurrencePattern: task.recurrencePattern || "",
    });
  };

  const handleEditSubmit = async () => {
    console.log("Edit submit - editTask:", editTask);
    console.log("Edit submit - formData:", formData);
    
    if (editTask && onEdit) {
      try {
        // Perform validation
        if (!formData.title.trim()) {
          alert("Title is required");
          return;
        }
        
        // Prepare data with proper format and defaults for edit
        const taskData: any = {
          id: editTask.id, // Include the task ID
          title: formData.title.trim(),
          description: formData.description ? formData.description.trim() : null,
          status: editTask.status || "pending",
          priority: formData.priority || "medium",
          recurring: Boolean(formData.recurring)
        };
        
        // Only include dueDate if it exists
        if (formData.dueDate) {
          taskData.dueDate = formData.dueDate;
        }
        
        // Only add recurrencePattern if task is recurring and pattern exists
        if (taskData.recurring && formData.recurrencePattern) {
          taskData.recurrencePattern = formData.recurrencePattern.trim();
        }
        
        console.log("Calling onEdit with:", editTask.id, taskData);
        await onEdit(editTask.id, taskData);
        setEditTask(null);
      } catch (error) {
        console.error("Error updating task:", error);
        alert(`Failed to update task. Please ensure you're logged in and try again.`);
      }
    }
  };

  const handleDeleteClick = (task: Task) => {
    setDeleteTask(task);
  };

  const handleDeleteConfirm = () => {
    if (deleteTask && onDelete) {
      onDelete(deleteTask.id);
      setDeleteTask(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-6 bg-neutral-100 rounded mb-3" />
              <div className="h-4 bg-neutral-100 rounded w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {tasks.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-neutral-500 mb-4">No tasks found.</p>
            {showAddButton && onAdd && (
              <Button onClick={openAddDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>
            )}
          </div>
        ) : (
          <>
            <ul className="space-y-3 divide-y divide-neutral-200">
              {tasks.map((task) => {
                const statusColor = getStatusColor(task.status);

                return (
                  <li key={task.id} className="py-3">
                    <div className="flex items-start">
                      {onComplete && (
                        <Checkbox
                          className="mt-1"
                          checked={task.status === "completed"}
                          disabled={isPendingComplete}
                          onCheckedChange={(checked) => onComplete(task.id, !!checked)}
                        />
                      )}
                      <div className="ml-3 flex-1">
                        <p className={`text-sm font-medium ${task.status === "completed" ? "line-through text-neutral-400" : "text-neutral-900"}`}>
                          {task.title}
                        </p>
                        {task.description && (
                          <p className="text-xs text-neutral-500 mt-1">{task.description}</p>
                        )}
                        {task.dueDate && (
                          <div className="flex items-center mt-1">
                            <Calendar className="text-neutral-400 h-3 w-3 mr-1" />
                            <span className="text-xs text-neutral-500">
                              Due {formatDate(task.dueDate)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-4 flex-shrink-0 flex gap-2 items-center">
                        <Badge className={`${statusColor.bg} ${statusColor.text}`}>
                          {task.status === "completed" ? "Completed" : task.status === "overdue" ? "Overdue" : "Due Soon"}
                        </Badge>
                        {task.priority !== "medium" && (
                          <Badge variant="outline" className={task.priority === "high" ? "border-red-200 text-red-700" : "border-green-200 text-green-700"}>
                            {task.priority === "high" ? "High" : "Low"}
                          </Badge>
                        )}
                        {onEdit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditClick(task)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {onDelete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(task)}
                            className="text-destructive"
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {showAddButton && onAdd && (
              <div className="mt-4">
                <Button onClick={openAddDialog} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Task
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Task Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={(open) => setIsAddDialogOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Task Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter task title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter task description"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dueDate">Due Date (Optional)</Label>
              <Input
                id="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger id="priority">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="recurring"
                checked={formData.recurring}
                onCheckedChange={(checked) => setFormData({ ...formData, recurring: checked })}
              />
              <Label htmlFor="recurring">Recurring Task</Label>
            </div>

            {formData.recurring && (
              <div className="space-y-2">
                <Label htmlFor="recurrencePattern">Recurrence Pattern</Label>
                <Select
                  value={formData.recurrencePattern}
                  onValueChange={(value) => setFormData({ ...formData, recurrencePattern: value })}
                >
                  <SelectTrigger id="recurrencePattern">
                    <SelectValue placeholder="Select pattern" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleAddSubmit} disabled={!formData.title || isPendingAdd}>
              {isPendingAdd ? "Adding..." : "Add Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={!!editTask} onOpenChange={(open) => !open && setEditTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Task Title</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description (Optional)</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-dueDate">Due Date (Optional)</Label>
              <Input
                id="edit-dueDate"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger id="edit-priority">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="edit-recurring"
                checked={formData.recurring}
                onCheckedChange={(checked) => setFormData({ ...formData, recurring: checked })}
              />
              <Label htmlFor="edit-recurring">Recurring Task</Label>
            </div>

            {formData.recurring && (
              <div className="space-y-2">
                <Label htmlFor="edit-recurrencePattern">Recurrence Pattern</Label>
                <Select
                  value={formData.recurrencePattern}
                  onValueChange={(value) => setFormData({ ...formData, recurrencePattern: value })}
                >
                  <SelectTrigger id="edit-recurrencePattern">
                    <SelectValue placeholder="Select pattern" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleEditSubmit} disabled={!formData.title || isPendingEdit}>
              {isPendingEdit ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Task Dialog */}
      <Dialog open={!!deleteTask} onOpenChange={(open) => !open && setDeleteTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              Are you sure you want to delete the task "{deleteTask?.title}"?
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isPendingDelete}
            >
              {isPendingDelete ? "Deleting..." : "Delete Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
